import { Container, Graphics, Text } from "pixi.js"

import { TextRenderer } from "../text/text_renderer"
import { blendColor } from "./flare"
import { HUD_PANEL_RIM, HUD_PANEL_TEXT_MUTED } from "./ui_tokens"

const WIDTH = 90
const TRACK_Y = 11
const TRACK_HEIGHT = 5

/** A small brass-inlaid gauge, sharing the hearts' timber plate. No sprite assets or filters. */
export class StaminaMeter extends Container {
    readonly max = 1
    private track = new Graphics()
    private fill = new Graphics()
    private label: Text
    private target = 1
    private shown = 1
    private initialized = false

    constructor() {
        super()
        this.label = new TextRenderer().fontSize(8).bold().color(0xffffff)
            .strokeThickness(0).render("STAMINA") as Text
        this.label.style.letterSpacing = 1
        this.label.style.padding = 2
        this.addChild(this.track, this.fill, this.label)
        // This fixed track also keeps the row's geometry steady at empty and full.
        this.track.lineStyle(1, HUD_PANEL_RIM, 0.65).beginFill(0x100c09, 0.95)
            .drawRoundedRect(0, TRACK_Y, WIDTH, TRACK_HEIGHT, 2).endFill()
        this.draw()
    }

    update = (ratio: number) => {
        this.target = Number.isFinite(ratio) ? Math.max(0, Math.min(1, ratio)) : 0
        if (!this.initialized) {
            this.shown = this.target
            this.initialized = true
            this.draw()
        }
    }

    animate = (deltaMs: number) => {
        if (!this.visible || this.shown === this.target) return
        // A brief settle makes individual attack costs readable without a distracting flash.
        const delta = this.target - this.shown
        this.shown += delta * (1 - Math.exp(-Math.max(0, Math.min(deltaMs, 100)) / 90))
        if (Math.abs(this.target - this.shown) < 0.001) this.shown = this.target
        this.draw()
    }

    private draw = () => {
        // Follow the animated fill, so hue and length settle together during drain and recovery.
        const color = this.shown <= 0.5
            ? blendColor(0xe36b60, 0xd8b46b, this.shown * 2)
            : blendColor(0xd8b46b, 0x91c78a, (this.shown - 0.5) * 2)
        this.label.tint = blendColor(0xe99688, HUD_PANEL_TEXT_MUTED, Math.min(1, this.shown / 0.25))
        this.fill.clear()
        const width = (WIDTH - 2) * this.shown
        if (width <= 0) return
        this.fill.beginFill(color, 0.95).drawRoundedRect(1, TRACK_Y + 1, width, TRACK_HEIGHT - 2,
            Math.min(1.5, width / 2)).endFill()
        // Fine, pale upper edge: a lit metal inlay rather than a flat neon rectangle.
        this.fill.beginFill(blendColor(color, 0xfff3e0, 0.55), 0.45)
            .drawRect(1, TRACK_Y + 1, width, 1).endFill()
    }
}
